"use client";

import { Folder, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import {
  AddToCartButton,
  EcommerceProductsProvider,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square rounded-lg bg-muted/50 animate-pulse" />
        <div className="space-y-6">
          <div className="h-10 w-3/4 rounded-md bg-muted/50 animate-pulse" />
          <div className="h-6 w-1/3 rounded-md bg-muted/50 animate-pulse" />
          <div className="h-24 w-full rounded-md bg-muted/50 animate-pulse" />
          <div className="h-12 w-full rounded-md bg-muted/50 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground mb-4">Unable to retrieve product data.</p>
        <Button onClick={refetch} variant="outline">
          Retry Connection
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Product currently unavailable.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    product.discounted_price !== product.price;

  return (
    <div data-index={0} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
      <div className="relative">
        <div className="sticky top-24">
          <div className="aspect-square overflow-hidden rounded-lg bg-card shadow-lg border border-border">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-muted">
                <span className="text-muted-foreground text-sm">Peptide Vial Placeholder</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        {product.categories && product.categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {product.categories.map((cat, i) => (
              <Link
                key={cat.id}
                to={cat.link || "#"}
                data-index={i}
                className="text-xs uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        )}

        <div className="bg-accent/5 border-2 border-border/40 rounded-lg p-8 mb-12">
          <h1
            className="font-default text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-foreground mb-6"
          >
            {product.title}
          </h1>

          <div className="flex items-baseline gap-4 mb-6">
            {hasDiscount ? (
              <>
                <span
                  className="text-3xl md:text-4xl font-default font-bold text-primary"
                >
                  ${Number(product.discounted_price).toFixed(2)}
                </span>
                <span className="text-xl text-muted-foreground line-through">
                  ${Number(product.price).toFixed(2)}
                </span>
                <Badge variant="default" className="bg-primary text-primary-foreground rounded-lg">
                  Sale
                </Badge>
              </>
            ) : (
              <span
                className="text-3xl md:text-4xl font-default font-bold text-foreground"
              >
                ${Number(product.price).toFixed(2)}
              </span>
            )}
          </div>

          {product.short_description && (
            <div
              className="text-base text-muted-foreground leading-relaxed mb-8 [&_p]:mb-2 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="mb-10">
            <AddToCartButton
              productId={product.id}
              variant="default"
              size="lg"
              className="w-full"
              buttonClassName="bg-primary text-primary-foreground w-full h-12 text-base font-default rounded-lg hover:bg-primary/90"
              text="Add to Cart"
              showQuantity={true}
            />
          </div>
        </div>

        {product.long_description && (
          <div className="bg-accent/5 border-2 border-border/40 rounded-lg p-8 mb-12">
            <h2 className="text-xl font-default font-semibold text-muted-foreground mb-4">
              Product Details
            </h2>
            <div
              className="text-base text-foreground/80 leading-relaxed [&_p]:mb-4 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-3 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        )}

        {product.customFields && Object.keys(product.customFields).length > 0 && (
          <div className="mt-12 mb-10 p-8 rounded-xl shadow-xl bg-secondary/80 border border-primary/30">
            <h2
              className="text-4xl font-default font-bold text-accent-foreground mb-2"
            >
              Technical Specifications
            </h2>
            <p className="text-muted-foreground mb-6">Detailed laboratory data and batch parameters.</p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {Object.entries(product.customFields).map(([key, value], i) => {
                if (value === null || value === undefined || value === "") return null;
                const displayValue =
                  typeof value === "object" ? JSON.stringify(value) : String(value);
                const label = key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase());
                return (
                  <div
                    key={key}
                    data-index={i}
                    className="flex flex-col border-b border-border/50 pb-3 last:border-b-0"
                  >
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      {label}
                    </dt>
                    <dd className="text-sm text-foreground font-medium">
                      {displayValue}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        )}

        <div className="space-y-4 pt-6 border-t border-border">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-start gap-3">
              <Folder className="size-4 mt-1 text-muted-foreground shrink-0" />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Category:</span>
                {product.categories.map((cat, i) => (
                  <Link
                    key={cat.id}
                    to={cat.link || "#"}
                    data-index={i}
                    className="text-sm text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline"
                  >
                    {cat.name}
                    {i < product.categories.length - 1 ? "," : ""}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="flex items-start gap-3">
              <Tag className="size-4 mt-1 text-muted-foreground shrink-0" />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Tags:</span>
                {product.tags.map((tag, i) => (
                  <Link key={tag.id} to={tag.link || "#"} data-index={i}>
                    <Badge
                      variant="secondary"
                      className="bg-secondary text-secondary-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      {tag.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Product() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  return (
    <section data-section-id="2125"
      id="single-product-template-section"
      className="bg-background text-foreground py-16 md:py-24"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <EcommerceProductsProvider wp_query={currentWPQueryParams}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}